(() => {
  class VelostoreDualMenu extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.overlay = this.querySelector('[data-velostore-menu-overlay]');
      this.panel = this.querySelector('.velostore-dual-menu__panel');
      this.openButtons = Array.from(this.querySelectorAll('[data-velostore-menu-open]'));
      this.closeButtons = Array.from(this.querySelectorAll('[data-velostore-menu-close]'));
      this.universeButtons = Array.from(this.querySelectorAll('[data-velostore-universe]'));
      this.universePanels = Array.from(this.querySelectorAll('[data-velostore-universe-panel]'));
      this.activeUniverse = this.dataset.defaultUniverse || 'cycling';
      this.activeOpener = null;
      this.previousBodyOverflow = '';
      this.previousBodyWidth = '';
      this.closeTimer = null;

      if (!this.overlay || !this.panel) return;

      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.open(button.dataset.velostoreMenuOpen, button);
        });
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.universeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.setUniverse(button.dataset.velostoreUniverse);
        });
      });

      this.universePanels.forEach((universePanel) => {
        universePanel.querySelectorAll('[data-velostore-section-tab]').forEach((button) => {
          button.addEventListener('click', () => {
            if (button.disabled) return;
            this.setSection(universePanel, button.dataset.velostoreSectionTab);
          });
        });
        this.mountCollections(universePanel);
        this.mountSpecial(universePanel);
      });

      this.handleThemeSectionLoad = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
          target.matches('[data-velostore-collections-source]') ||
          target.querySelector('[data-velostore-collections-source]')
        ) {
          this.universePanels.forEach((panel) => this.mountCollections(panel, true));
        }
        if (
          target.matches('[data-velostore-special-source]') ||
          target.querySelector('[data-velostore-special-source]')
        ) {
          this.universePanels.forEach((panel) => this.mountSpecial(panel, true));
        }
      };

      document.addEventListener('shopify:section:load', this.handleThemeSectionLoad);
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href]');
        if (link && this.panel.contains(link)) {
          this.close({ restoreFocus: false });
        }
      });
    }

    disconnectedCallback() {
      if (this.handleThemeSectionLoad) {
        document.removeEventListener('shopify:section:load', this.handleThemeSectionLoad);
      }
    }

    open(universe, opener) {
      window.clearTimeout(this.closeTimer);
      this.activeOpener = opener || null;
      this.setUniverse(universe || this.activeUniverse);

      this.overlay.hidden = false;
      this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (this.activeOpener) this.activeOpener.setAttribute('aria-expanded', 'true');

      this.previousBodyOverflow = document.body.style.overflow;
      this.previousBodyWidth = document.body.style.width;
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';
      document.body.classList.add('velostore-dual-menu-is-open');

      const header = document.getElementById('header');
      header?.classList.add('menu-open', 'is-megamenu-open');

      requestAnimationFrame(() => {
        this.overlay.classList.add('is-open');
        const firstFocusable = this.getFocusableElements()[0];
        (firstFocusable || this.panel).focus({ preventScroll: true });
      });
    }

    close(options = {}) {
      const { restoreFocus = true } = options;
      if (this.overlay.hidden) return;

      this.overlay.classList.remove('is-open');
      this.openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = this.previousBodyOverflow;
      document.body.style.width = this.previousBodyWidth;
      document.body.classList.remove('velostore-dual-menu-is-open');

      const header = document.getElementById('header');
      header?.classList.remove('menu-open', 'is-megamenu-open');

      this.closeTimer = window.setTimeout(() => {
        this.overlay.hidden = true;
        if (restoreFocus && this.activeOpener) {
          this.activeOpener.focus({ preventScroll: true });
        }
      }, 430);
    }

    setUniverse(universe) {
      const availablePanel = this.universePanels.find(
        (panel) => panel.dataset.velostoreUniversePanel === universe
      );
      const targetPanel = availablePanel || this.universePanels[0];
      if (!targetPanel) return;

      this.activeUniverse = targetPanel.dataset.velostoreUniversePanel;

      this.universeButtons.forEach((button) => {
        const isActive = button.dataset.velostoreUniverse === this.activeUniverse;
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      this.universePanels.forEach((panel) => {
        panel.hidden = panel !== targetPanel;
      });

      this.mountCollections(targetPanel);
      this.mountSpecial(targetPanel);

      const activeSectionButton = targetPanel.querySelector(
        '[data-velostore-section-tab][aria-selected="true"]:not(:disabled)'
      );
      const firstAvailableSectionButton = targetPanel.querySelector(
        '[data-velostore-section-tab]:not(:disabled)'
      );
      const sectionButton = activeSectionButton || firstAvailableSectionButton;
      if (sectionButton) {
        this.setSection(targetPanel, sectionButton.dataset.velostoreSectionTab);
      }
    }

    setSection(universePanel, section) {
      const tabButtons = Array.from(
        universePanel.querySelectorAll('[data-velostore-section-tab]')
      );
      const contentPanels = Array.from(
        universePanel.querySelectorAll('[data-velostore-section-panel]')
      );

      let targetButton = tabButtons.find(
        (button) => button.dataset.velostoreSectionTab === section && !button.disabled
      );
      if (!targetButton) targetButton = tabButtons.find((button) => !button.disabled);
      if (!targetButton) return;

      const targetSection = targetButton.dataset.velostoreSectionTab;

      tabButtons.forEach((button) => {
        const isActive = button === targetButton;
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      contentPanels.forEach((panel) => {
        panel.hidden = panel.dataset.velostoreSectionPanel !== targetSection;
      });

      this.panel.classList.toggle('is-collections', targetSection === 'collections');

      if (targetSection === 'collections') {
        this.mountCollections(universePanel);
        this.resetCollectionPreview(universePanel);
      }

      if (targetSection === 'use') {
        this.mountSpecial(universePanel);
      }
    }

    mountSpecial(universePanel, force = false) {
      const mount = universePanel.querySelector('[data-velostore-special-mount]');
      const universe = universePanel.dataset.velostoreUniversePanel;
      if (!mount || !universe) return;

      const source = document.querySelector('[data-velostore-special-source]');
      const selector = universe === 'cycling'
        ? '[data-velostore-racks-template]'
        : '[data-velostore-club-template]';
      const template = source?.querySelector(selector);
      if (!(template instanceof HTMLTemplateElement)) return;

      const sourceId = source.dataset.sectionId || 'special-source';
      if (!force && mount.dataset.sourceId === sourceId && mount.dataset.mounted === 'true') {
        return;
      }

      mount.replaceChildren(template.content.cloneNode(true));
      mount.dataset.sourceId = sourceId;
      mount.dataset.mounted = 'true';
    }

    mountCollections(universePanel, force = false) {
      const mount = universePanel.querySelector('[data-velostore-collections-mount]');
      const universe = universePanel.dataset.velostoreUniversePanel;
      if (!mount || !universe) return;

      const source = document.querySelector('[data-velostore-collections-source]');
      const template = source?.querySelector(
        `[data-velostore-collections-template="${CSS.escape(universe)}"]`
      );
      if (!(template instanceof HTMLTemplateElement)) return;

      const sourceId = source.dataset.sectionId || 'collections-source';
      if (!force && mount.dataset.sourceId === sourceId && mount.dataset.mounted === 'true') {
        return;
      }

      mount.replaceChildren(template.content.cloneNode(true));
      mount.dataset.sourceId = sourceId;
      mount.dataset.mounted = 'true';
      this.initializeCollectionPreview(mount);
    }

    initializeCollectionPreview(scope) {
      const items = Array.from(scope.querySelectorAll('[data-velostore-collection-item]'));
      if (!items.length) return;

      items.forEach((item) => {
        item.addEventListener('mouseenter', () => this.updateCollectionPreview(scope, item));
        item.addEventListener('focus', () => this.updateCollectionPreview(scope, item));
      });
    }

    updateCollectionPreview(scope, item) {
      const previewCard = scope.querySelector('[data-velostore-collection-preview-card]');
      const previewImage = scope.querySelector('[data-velostore-collection-preview-image]');
      const previewMedia = scope.querySelector('[data-velostore-collection-preview-media]');
      const previewTitle = scope.querySelector('[data-velostore-collection-preview-title]');
      const previewBadges = scope.querySelector('[data-velostore-collection-preview-badges]');
      if (!previewCard || !previewImage || !previewMedia || !previewTitle || !previewBadges) return;

      scope.querySelectorAll('[data-velostore-collection-item]').forEach((collectionItem) => {
        collectionItem.classList.toggle('is-active', collectionItem === item);
      });

      const title = item.dataset.collectionTitle || '';
      const image = item.dataset.collectionImage || '';
      const imageAlt = item.dataset.collectionImageAlt || title;
      const link = item.dataset.collectionLink || '';
      // Numeric suffixes in data attributes (for example data-collection-badge-1)
      // are not exposed reliably as dot-notation dataset keys in every browser.
      // Read the attributes directly so all three configured badges render.
      const badges = [
        item.getAttribute('data-collection-badge-1'),
        item.getAttribute('data-collection-badge-2'),
        item.getAttribute('data-collection-badge-3'),
      ].filter((badge) => typeof badge === 'string' && badge.trim().length > 0);

      previewTitle.textContent = title;
      previewImage.alt = imageAlt;
      previewMedia.classList.toggle('is-empty', !image);
      if (image) {
        previewImage.src = image;
      } else {
        previewImage.removeAttribute('src');
      }

      if (link) {
        previewCard.href = link;
      } else {
        previewCard.removeAttribute('href');
      }

      previewBadges.replaceChildren(
        ...badges.map((badge) => {
          const span = document.createElement('span');
          span.textContent = badge;
          return span;
        })
      );

      previewCard.hidden = false;
    }

    resetCollectionPreview(universePanel) {
      const scope = universePanel.querySelector('[data-velostore-collections-mount]');
      if (!scope) return;
      scope.querySelectorAll('[data-velostore-collection-item]').forEach((item) => {
        item.classList.remove('is-active');
      });
      const previewCard = scope.querySelector('[data-velostore-collection-preview-card]');
      if (previewCard) previewCard.hidden = true;
    }

    handleKeydown(event) {
      if (this.overlay.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements() {
      return Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);
    }
  }

  if (!customElements.get('velostore-dual-menu')) {
    customElements.define('velostore-dual-menu', VelostoreDualMenu);
  }
})();
